import React, { useCallback, useEffect, useState } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import toast from "react-hot-toast";

const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_PAYPAL_SECRET_KEY;
const PARTNER_ID = process.env.REACT_APP_PAYPAL_PARTNER_ID || "5ZPAUD3E2QNVY";
const PAYPAL_API = "https://api-m.paypal.com";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://chefrepublicwebapi-dev.portalwiz.in/api";

/**
 * Strips all HTML tags from a string and removes any bare URLs left behind by <a> tags.
 */
const stripHtml = (html) =>
  html
    ? html
        .replace(/<a\b[^>]*>.*?<\/a>/gi, "")
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const getPayPalAccessToken = async () => {
  const basicAuth = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);
  const res = await fetch(`${PAYPAL_API}/v1/oauth2/token`, {
    method: "POST",
    headers: {
      Authorization: `Basic ${basicAuth}`,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: "grant_type=client_credentials",
  });
  const data = await res.json();
  return data.access_token;
};

export default function LinkPayment({
  isPaymentLinked,
  paymentAccountDetails,
  syncStatusDetails,
  className,
  style,
  onAddChefAccountSuccess,
}) {
  const userId = localStorage.getItem("logged_user_id");

  const [loading, setLoading] = useState(false);
  const [showPaymentsReceivableError, setShowPaymentsReceivableError] =
    useState(false);
  const [showEmailConfirmationError, setShowEmailConfirmationError] =
    useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Check if all PayPal status conditions are met
  const isPayPalFullyReady = syncStatusDetails?.success === true && 
                            syncStatusDetails.primary_email_confirmed === true && 
                            syncStatusDetails.payments_receivable === true;

  // Popups based on syncStatusDetails (priority) or fallback to paymentAccountDetails
  useEffect(() => {
    // If no account exists, no popups
    if (!paymentAccountDetails || paymentAccountDetails.status !== true) {
      setShowPaymentsReceivableError(false);
      setShowEmailConfirmationError(false);
      setShowSuccessDialog(false);
      return;
    }

    // Use syncStatusDetails if available (even if success is false, we use its fields)
    if (syncStatusDetails) {
      const {
        primary_email_confirmed,
        payments_receivable,
        message: syncMessage,
      } = syncStatusDetails;

      if (!primary_email_confirmed) {
        setErrorMessage(
          syncMessage ||
            "Please confirm your PayPal email address to continue receiving payments."
        );
        setShowEmailConfirmationError(true);
        setShowPaymentsReceivableError(false);
        setShowSuccessDialog(false);
      } else if (!payments_receivable) {
        setErrorMessage(
          syncMessage ||
            "Your PayPal account cannot receive payments. Please complete your account setup."
        );
        setShowPaymentsReceivableError(true);
        setShowEmailConfirmationError(false);
        setShowSuccessDialog(false);
      } else {
        // Fully ready
        setShowSuccessDialog(true);
        setShowEmailConfirmationError(false);
        setShowPaymentsReceivableError(false);

        const timer = setTimeout(() => setShowSuccessDialog(false), 5000);
        return () => clearTimeout(timer);
      }
    } else {
      // Fallback to get_chef_account_details emailStatus
      const emailStatus = String(paymentAccountDetails.emailStatus || "")
        .replace(/[\s_]/g, "")
        .toUpperCase();

      if (emailStatus === "NOTCONFIRMED") {
        setErrorMessage(
          paymentAccountDetails.message ||
            "Please confirm your PayPal email address to continue receiving payments."
        );
        setShowEmailConfirmationError(true);
        setShowPaymentsReceivableError(false);
        setShowSuccessDialog(false);
      } else if (emailStatus === "CONFIRMED") {
        setShowSuccessDialog(true);
        setShowEmailConfirmationError(false);
        setShowPaymentsReceivableError(false);

        const timer = setTimeout(() => setShowSuccessDialog(false), 5000);
        return () => clearTimeout(timer);
      } else {
        // Unknown state – hide all
        setShowPaymentsReceivableError(false);
        setShowEmailConfirmationError(false);
        setShowSuccessDialog(false);
      }
    }
  }, [paymentAccountDetails, syncStatusDetails]);

  const openPayPalInNewTab = () => {
    window.open("https://www.paypal.com", "_blank", "noopener,noreferrer");
  };

  const saveMerchantIdToBackend = useCallback(
    async (merchantId, email) => {
      try {
        const res = await fetch(`${API_BASE_URL}/user/add_chef_account`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            merchantId,
            Email: email,
            merchantIdInPayPal: merchantId,
            permissionsGranted: true,
            accountStatus: "",
            consentStatus: "",
            isEmailConfirmed: "",
            returnMessage: "",
          }),
        });
        const data = await res.json();

        if (data.status === true) {
          toast.success("PayPal account connected successfully!");
          onAddChefAccountSuccess?.();
        } else {
          toast.error(data.message || "Something went wrong. Please try again.");
        }
      } catch (err) {
        console.error("Save merchant error:", err);
        toast.error("Failed to save merchant account. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [userId, onAddChefAccountSuccess]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const merchantId = params.get("merchantIdInPayPal");
    const email = params.get("email");

    if (merchantId) {
      setLoading(true);
      saveMerchantIdToBackend(merchantId, email);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [saveMerchantIdToBackend]);

  const handleOnboarding = async () => {
    setLoading(true);
    try {
      const accessToken = await getPayPalAccessToken();
      const currentPath = window.location.pathname.replace(/^\//, "");

      const res = await fetch(`${API_BASE_URL}/user/partner_referral`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          operations: [
            {
              operation: "API_INTEGRATION",
              api_integration_preference: {
                rest_api_integration: {
                  integration_method: "PAYPAL",
                  integration_type: "THIRD_PARTY",
                },
              },
            },
          ],
          products: ["PPCP", "ADVANCED_VAULTING"],
          capabilities: ["PAYPAL_WALLET_VAULTING_ADVANCED"],
          partner_config_override: {
            return_url: `${window.location.origin}/${currentPath}`,
            return_url_description: "Back to Chef Republic",
          },
        }),
      });
      const data = await res.json();

      if (data?.signup_link) {
        window.location.href = data.signup_link;
      } else {
        toast.error("PayPal onboarding failed. Please try again.");
      }
    } catch (err) {
      console.error("Onboarding error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <button
        type="button"
        className={className || "btn btn-orange rounded-pill mb-3"}
        disabled={isPayPalFullyReady || loading}
        onClick={handleOnboarding}
        style={{
          boxSizing: "border-box",
          maxWidth: "100%",
          width: "100%",
          backgroundColor: isPayPalFullyReady ? "#cccbcb" : "#FF5E41",
          color: "#fff",
          cursor: isPayPalFullyReady || loading ? "not-allowed" : "pointer",
          ...style,
        }}
      >
        {loading ? (
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
        ) : (
          "Link Payment Method"
        )}
      </button>

      {/* Dialog 1 – Payments Receivable Issue */}
      <Dialog
        open={showPaymentsReceivableError}
        onClose={() => setShowPaymentsReceivableError(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Account Setup Required
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 2, color: "#666" }}>
            {stripHtml(errorMessage)}
          </Typography>

          <a
            href="https://www.paypal.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#4441ff",
              textDecoration: "underline",
              fontSize: "14px",
            }}
          >
            www.paypal.com
          </a>

          <Box sx={{ mt: 3 }}>
            <button
              onClick={openPayPalInNewTab}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              Go to PayPal
            </button>
          </Box>
        </Box>
      </Dialog>

      {/* Dialog 2 – Email Confirmation Required */}
      <Dialog
        open={showEmailConfirmationError}
        onClose={() => setShowEmailConfirmationError(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Confirmation Required
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
            {stripHtml(errorMessage)}
          </Typography>

          <Box>
            <button
              onClick={openPayPalInNewTab}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              Confirm Email Address
            </button>
          </Box>
        </Box>
      </Dialog>

      {/* Dialog 3 – Success */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                backgroundColor: "#4CAF50",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography sx={{ color: "#fff", fontSize: "30px" }}>✓</Typography>
            </Box>
          </Box>

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, textAlign: "center", mb: 1 }}
          >
            Ready to Receive Payments!
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 3, color: "#666", textAlign: "center" }}
          >
            Your PayPal account is fully set up and ready to receive payments from
            customers.
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
}